import { useEffect, useMemo, useState } from "react";
import "./App.scss";
import { ConfigProvider, Layout, notification } from "antd";
import { AppFooter, AppHeader, CustomEmpty } from "../components";
import AppRoutes from "./AppRoutes";
import { getMe } from "../features/auth/authActions";
import { logOut } from "../features/auth/authSlice";
import isStaffRole from "../common/services/permissions/isStaffRole";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { AgeConfirmationModal } from "../modals";
import useThemeManager from "../hooks/useThemeManager";
import { withButtonBorder, withDrawerLine } from "../hooks/useShopTheme";
import { getShopDetails } from "../features/shopDetails/shopDetailsActions";
import { getBasket, mergeBasket } from "../features/card/cardActions";
import LocalStorageService from "../common/services/LocalStorageService";
import useShopSocket from "../common/services/useShopSocket";

export default function App() {
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const groups = useSelector((state) => state?.auth?.groups);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const { currentTheme, themeReady } = useThemeManager();
  const antdTheme = useMemo(
    () => withButtonBorder(withDrawerLine(currentTheme)),
    [currentTheme]
  );
  // Set once the settings request has finished, fulfilled or not. A shop
  // whose settings fail to load (or come back empty) still gets the default
  // theme instead of an empty page.
  const [settingsSettled, setSettingsSettled] = useState(false);
  // One websocket per logged-in customer, opened here at the app root (never
  // per order). It feeds the order chat threads and unread badges.
  useShopSocket();

  useEffect(() => {
    if (isAuthenticated && isStaffRole(groups)) {
      api.error({
        title: "Staff accounts are not permitted here",
        description:
          "This storefront is reserved for customer shopping. You have been signed out. Please use the dedicated staff portal to continue.",
        placement: "top",
        duration: 6,
      });
      dispatch(logOut());
      navigate("/");
      return;
    }
    if (isAuthenticated) {
      dispatch(getMe());
    }
  }, [dispatch, isAuthenticated, groups, api, navigate]);

  useEffect(() => {
    // Server basket sync — runs on login and on app boot while logged in.
    // Guest-built localStorage items are merged into the server basket once
    // (the merge thunk clears the local copy on success); otherwise just
    // hydrate the cart from the server.
    if (isAuthenticated && !isStaffRole(groups)) {
      const localItems = LocalStorageService.get("items");
      if (localItems?.length) {
        dispatch(mergeBasket(localItems)).then((res) => {
          if (res?.type === "basket/merge/fulfilled") {
            api.success({
              title: "Cart saved",
              description: `${localItems.length} item${
                localItems.length > 1 ? "s" : ""
              } from your cart ${
                localItems.length > 1 ? "were" : "was"
              } saved to your account.`,
              placement: "top",
            });
          }
        });
      } else {
        dispatch(getBasket());
      }
    }
  }, [dispatch, isAuthenticated, groups, api]);

  useEffect(() => {
    // The theme manager applies the merged theme and its CSS variables; this
    // only tracks when the request has settled.
    const settle = () => setSettingsSettled(true);
    Promise.resolve(dispatch(getShopDetails())).then(settle, settle);
  }, [dispatch]);

  // With a cached theme the body renders immediately. Without one (a fresh
  // browser) only the background shows until the settings arrive, so the
  // visitor never sees the default theme flash before the shop's own colors.
  const showBody = themeReady || settingsSettled;

  return (
    <ConfigProvider theme={antdTheme} renderEmpty={() => <CustomEmpty />}>
      {contextHolder}
      {showBody ? (
        <Layout className="App-cont" data-testid="app-container">
          <AppHeader />
          <Layout.Content className="App-cont__content">
            <AppRoutes />
          </Layout.Content>
          <AppFooter />
          <AgeConfirmationModal />
        </Layout>
      ) : null}
    </ConfigProvider>
  );
}
